import { useState } from "react";

import { usePreviewRunServicesQuery } from "@/api/previewInsights";
import { featureEntries, serviceFlagEntries } from "@/lib/labServices";
import { Button } from "@/ui/Button";
import { ErrorState, LoadingState } from "@/ui/states";

import { LabAbDialog } from "../LabAbDialog";
import { LabFeatureGroups } from "../LabFeatureGroups";
import { LabServiceCall } from "../LabServicesPanel";

/**
 * Whole-run Services: the pack features this run resolved, and every service
 * call of every turn. "A/B" runs the same persona on this testing
 * version and on a copy that differs in one pack feature, or twice on the same testing version differing in one run option.
 */
export function LabRunServicesTab({
  projectId,
  previewRunRef,
  live,
  onSelectTurn,
  onOpenRuns,
}: {
  projectId: string;
  previewRunRef: string;
  live: boolean;
  onSelectTurn: (turn: number) => void;
  onOpenRuns: (runRef: string, compareRef: string) => void;
}) {
  const query = usePreviewRunServicesQuery(projectId, previewRunRef, { live });
  const [abOpen, setAbOpen] = useState(false);
  if (query.isPending) {
    return <LoadingState label="Loading services" rows={3} />;
  }
  if (query.isError) {
    return (
      <ErrorState
        title="Services could not be read"
        onRetry={() => void query.refetch()}
      />
    );
  }
  const features = featureEntries(query.data);
  const serviceFlags = serviceFlagEntries(query.data);
  const turns = query.data.turns;
  return (
    <div className="space-y-4">
      <section aria-label="Features" className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Features this run used</h3>
          <Button variant="outline" size="sm" onClick={() => setAbOpen(true)}>
            A/B
          </Button>
        </div>
        <LabFeatureGroups features={features} serviceFlags={serviceFlags} />
      </section>
      <section aria-label="Service calls" className="space-y-2">
        <h3 className="text-sm font-semibold">Service calls by turn</h3>
        {turns.length === 0 ? (
          <p role="status" className="text-sm text-muted-foreground">
            No service calls were recorded for this run.
          </p>
        ) : (
          <ul className="space-y-3">
            {turns.map((turn) => (
              <li key={turn.turn} className="space-y-1">
                <button
                  type="button"
                  onClick={() => onSelectTurn(turn.turn)}
                  className="text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
                >
                  Turn {turn.turn}
                </button>
                {turn.calls.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No service calls.
                  </p>
                ) : (
                  turn.calls.map((call) => (
                    <LabServiceCall key={call.service} call={call} />
                  ))
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      {abOpen ? (
        <LabAbDialog
          projectId={projectId}
          previewRunRef={previewRunRef}
          features={features}
          serviceFlags={serviceFlags}
          onClose={() => setAbOpen(false)}
          onDone={onOpenRuns}
        />
      ) : null}
    </div>
  );
}
